/**
 * Auth guard helper for TanStack Router beforeLoad.
 *
 * Pure function — testable without Clerk or React.
 *
 * Public paths (no auth required):
 *  - /auth/*              (Clerk auth pages)
 *  - /sso/callback/*      (OAuth callbacks)
 *  - /valiant             (legacy redirect)
 *  - /                    (root — has its own redirect)
 *  - /$lang/$companyId/public/*  (all public routes incl. demo, expose, contact, auth sub-routes)
 *  - /$lang/$companyId/partner.$externalReference/result  (partner calculator result)
 *
 * Protected paths: everything else under /$lang/$companyId/ that is NOT under /public/.
 */

/**
 * Returns true when the guard SHOULD redirect to login.
 *
 * @param pathname  Current location.pathname
 * @param isLoaded  Clerk isLoaded flag (false = Clerk still initializing)
 * @param isSignedIn Clerk isSignedIn flag
 */
export function shouldRedirectToLogin(
  pathname: string,
  isLoaded: boolean,
  isSignedIn: boolean,
): boolean {
  // Never redirect while Clerk is still loading — prevents false redirects
  // for authenticated users during the brief window before Clerk hydrates.
  if (!isLoaded) {
    return false;
  }

  // Signed-in users pass through
  if (isSignedIn) {
    return false;
  }

  // Check if the path is public
  return !isPublicPath(pathname);
}

/**
 * Public path patterns that do NOT require authentication.
 */
export function isPublicPath(pathname: string): boolean {
  // Root path — has its own redirect logic in $lang.$companyId/index.tsx
  if (pathname === '/') {
    return true;
  }

  // /auth/* — Clerk auth pages (login, oauth, success, failure)
  if (pathname.startsWith('/auth/') || pathname === '/auth') {
    return true;
  }

  // /sso/callback/* — OAuth callbacks
  if (pathname.startsWith('/sso/callback/')) {
    return true;
  }

  // /valiant — legacy redirect
  if (pathname === '/valiant' || pathname.startsWith('/valiant/')) {
    return true;
  }

  // /$lang/$companyId/public/* — all public routes
  // Pattern: /{lang}/{companyId}/public/...
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 3 && segments[2] === 'public') {
    return true;
  }

  // /$lang/$companyId/partner.$externalReference/result — partner calculator result
  if (
    segments.length >= 4 &&
    segments[2]?.startsWith('partner.') &&
    segments[3] === 'result'
  ) {
    return true;
  }

  return false;
}
